import { logger } from 'utils/logger';

/**
 * Recovers a page left open across a deploy. Every route is a lazy chunk with a
 * content-hashed name, and a deploy removes the old names. A page built against
 * the previous deploy then requests a chunk that no longer exists, Hosting's SPA
 * rewrite answers with index.html, and the import fails - which the
 * ErrorBoundary would show as "Something went wrong". Vite reports that failure
 * as `vite:preloadError`; reloading fetches the current index.html (served
 * no-cache, see firebase.json) and so the current chunk names.
 *
 * At most one reload per window of `RELOAD_WINDOW_MS`, so a chunk that is
 * genuinely broken in the current deploy falls through to the ErrorBoundary
 * instead of reloading forever. Unattended screens matter most here: nobody is
 * there to press Reload.
 */
const LAST_RELOAD_KEY = 'stale-chunk-reload-at';
const RELOAD_WINDOW_MS = 60_000;

export function registerStaleChunkReload(win: Window = window): void {
  win.addEventListener('vite:preloadError', (event) => {
    let last = 0;
    try {
      last = Number(win.sessionStorage.getItem(LAST_RELOAD_KEY)) || 0;
    } catch {
      return; // no storage means no loop guard, so leave it to the ErrorBoundary
    }
    const now = Date.now();
    if (now - last < RELOAD_WINDOW_MS) {
      logger.error('Chunk failed to load again after a reload', {
        context: 'reloadOnStaleChunk',
        error: (event as Event & { payload?: unknown }).payload,
      });
      return;
    }
    win.sessionStorage.setItem(LAST_RELOAD_KEY, String(now));
    event.preventDefault();
    logger.warn('Chunk from a previous deploy failed to load; reloading', { context: 'reloadOnStaleChunk' });
    win.location.reload();
  });
}
